import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Search, User, Plus, X, Save, Pencil, Power, KeyRound, Trash2 } from "lucide-react";
import ConfirmDialog from "../components/ConfirmDialog";

const FULL_ROLES = new Set(["super_admin", "admin", "manager"]);
const MANAGEABLE_ROLES = [
  { value: "manager", label: "Manager" },
  { value: "vet", label: "Vet" },
  { value: "groom", label: "Groom" },
  { value: "driver", label: "Driver" },
  { value: "handyman", label: "Handyman" },
  { value: "cleaner", label: "Cleaner" },
  { value: "gardener", label: "Gardener" },
  { value: "farrier", label: "Farrier" },
  { value: "dentist", label: "Dentist" },
  { value: "chiropractor", label: "Chiropractor" },
  { value: "owner", label: "Owner" },
];
const ROLE_FILTERS = [
  { value: "all", label: "All" },
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager" },
  { value: "vet", label: "Vet" },
  { value: "groom", label: "Groom" },
  { value: "driver", label: "Driver" },
  { value: "handyman", label: "Handyman" },
  { value: "cleaner", label: "Cleaner" },
  { value: "gardener", label: "Gardener" },
  { value: "farrier", label: "Farrier" },
  { value: "dentist", label: "Dentist" },
  { value: "chiropractor", label: "Chiropractor" },
  { value: "owner", label: "Owner" },
];

const roleBadge = (r) => {
  switch (r) {
    case "admin":   return "se-badge se-badge-active";
    case "manager": return "se-badge se-badge-active";
    case "vet":     return "se-badge se-badge-warning";
    case "groom":   return "se-badge se-badge-inactive";
    case "driver":  return "se-badge se-badge-inactive";
    case "owner":   return "se-badge se-badge-inactive";
    default:        return "se-badge se-badge-inactive";
  }
};

const initials = (name) => (name || "?").split(/\s+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

export default function Staff() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const canManage = FULL_ROLES.has(me?.role);
  const isAdmin = me?.role === "admin" || me?.role === "super_admin";

  const [users, setUsers] = useState(null);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [confirm, setConfirm] = useState(null); // { kind: 'suspend'|'reactivate'|'remove', user }

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      setError(formatApiError(e));
      setUsers([]);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (users || []).filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (statusFilter !== "all" && u.status !== statusFilter) return false;
      if (!q) return true;
      return (
        (u.full_name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q)
      );
    });
  }, [users, search, roleFilter, statusFilter]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 4000);
  };

  const onAdded = (newUser) => {
    setShowAdd(false);
    showToast(`Staff member added. Share their login details with ${newUser.full_name} directly.`);
    load();
  };
  const onEdited = () => { setEditTarget(null); showToast("Staff updated."); load(); };
  const onResetDone = (u) => { setResetTarget(null); showToast(`Password updated. Share the new password with ${u.full_name} directly.`); load(); };

  const doAction = async () => {
    if (!confirm) return;
    const u = confirm.user;
    try {
      if (confirm.kind === "suspend") {
        await api.post(`/users/${u.id}/suspend`);
        showToast(`${u.full_name} suspended.`);
      } else if (confirm.kind === "reactivate") {
        await api.post(`/users/${u.id}/reactivate`);
        showToast(`${u.full_name} reactivated.`);
      } else if (confirm.kind === "remove") {
        await api.post(`/users/${u.id}/remove`);
        showToast(`${u.full_name} removed.`);
      }
      setConfirm(null);
      await load();
    } catch (e) {
      setError(formatApiError(e));
      setConfirm(null);
    }
  };

  return (
    <div data-testid="staff-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <div className="se-label">Team</div>
          <h1 className="font-heading mt-1" style={{ fontSize: 32 }}>Staff</h1>
          <p className="text-sm mt-1" style={{ color: "#8C9E8A" }}>
            Add staff, set roles, and manage access. Suspended or removed users are immediately logged out.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <button className="se-btn" onClick={() => setShowAdd(true)} data-testid="staff-add-button">
              <Plus size={14} /> Add staff member
            </button>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div className="se-card mb-4 p-3 flex flex-wrap items-center gap-3" data-testid="staff-filter-bar">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#8C9E8A" }} />
          <input className="se-input" style={{ paddingLeft: 36 }} placeholder="Search name or email"
            value={search} onChange={(e) => setSearch(e.target.value)} data-testid="staff-search-input" />
        </div>
        <select className="se-input" style={{ width: 140 }} value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="staff-role-filter">
          {ROLE_FILTERS.map((r) => <option key={r.value} value={r.value}>{r.label === "All" ? "All roles" : r.label}</option>)}
        </select>
        <select className="se-input" style={{ width: 160 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="staff-status-filter">
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {error && <div className="se-card mb-3" style={{ color: "#922B21" }} data-testid="staff-error">{error}</div>}
      {toast && <div className="se-card mb-3" style={{ background: "rgba(74,124,89,0.1)", color: "#2D5E3A" }} data-testid="staff-toast">{toast}</div>}

      <div className="se-card p-0 overflow-hidden" data-testid="staff-table-card">
        <div className="overflow-x-auto">
          <table className="se-table w-full" data-testid="staff-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Last login</th>
                {canManage && <th className="text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {users === null && <tr><td colSpan={canManage ? 7 : 6} style={{ color: "#8C9E8A" }}>Loading…</td></tr>}
              {users !== null && filtered.length === 0 && (
                <tr>
                  <td colSpan={canManage ? 7 : 6} style={{ color: "#8C9E8A" }} data-testid="staff-empty">
                    No staff match your filters.
                  </td>
                </tr>
              )}
              {filtered.map((u) => {
                const isSelf = u.id === me?.id;
                const canActOnTarget = canManage && (me.role === "super_admin" || me.role === "admin" || (me.role === "manager" && u.role !== "admin"));
                return (
                  <tr key={u.id} data-testid={`staff-row-${u.id}`}>
                    <td style={{ fontWeight: 600 }}>
                      <span className="inline-flex items-center gap-2">
                        <span className="w-8 h-8 rounded-full inline-flex items-center justify-center text-xs"
                          style={{ background: "#7C5C3A", color: "#F5EFE6", fontWeight: 700 }}>{initials(u.full_name)}</span>
                        <button onClick={() => navigate(`/staff/${u.id}`)} className="se-link text-left" data-testid={`staff-name-${u.id}`}>{u.full_name}</button>
                      </span>
                    </td>
                    <td><span className={roleBadge(u.role)} data-testid={`staff-role-${u.id}`}>{u.role}</span></td>
                    <td>{u.email}</td>
                    <td style={{ color: "#8C9E8A" }}>{u.phone || "—"}</td>
                    <td>
                      <span className={`se-badge ${u.status === "active" ? "se-badge-active" : "se-badge-inactive"}`} data-testid={`staff-status-${u.id}`}>
                        {u.status}
                      </span>
                    </td>
                    <td style={{ color: "#8C9E8A" }}>
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
                    </td>
                    {canManage && (
                      <td>
                        <div className="flex gap-1 justify-end">
                          {canActOnTarget && !isSelf && (
                            <button className="se-btn se-btn-ghost" style={{ padding: 6 }} onClick={() => setEditTarget(u)} title="Edit" data-testid={`staff-edit-${u.id}`}><Pencil size={14} /></button>
                          )}
                          {canActOnTarget && !isSelf && u.status === "active" && (
                            <button className="se-btn se-btn-ghost" style={{ padding: 6, color: "#C0392B" }} onClick={() => setConfirm({ kind: "suspend", user: u })} title="Suspend" data-testid={`staff-suspend-${u.id}`}><Power size={14} /></button>
                          )}
                          {canActOnTarget && !isSelf && u.status === "inactive" && (
                            <button className="se-btn se-btn-ghost" style={{ padding: 6, color: "#2D5E3A" }} onClick={() => setConfirm({ kind: "reactivate", user: u })} title="Reactivate" data-testid={`staff-reactivate-${u.id}`}><Power size={14} /></button>
                          )}
                          {canActOnTarget && !isSelf && (
                            <button className="se-btn se-btn-ghost" style={{ padding: 6 }} onClick={() => setResetTarget(u)} title="Reset password" data-testid={`staff-reset-${u.id}`}><KeyRound size={14} /></button>
                          )}
                          {isAdmin && !isSelf && u.role !== "admin" && (
                            <button className="se-btn se-btn-ghost" style={{ padding: 6, color: "#C0392B" }} onClick={() => setConfirm({ kind: "remove", user: u })} title="Remove" data-testid={`staff-remove-${u.id}`}><Trash2 size={14} /></button>
                          )}
                          {isAdmin && !isSelf && u.role === "admin" && (
                            <span className="text-xs" style={{ color: "#8C9E8A", paddingLeft: 8 }}>Admin</span>
                          )}
                          {isSelf && <span className="text-xs" style={{ color: "#8C9E8A", paddingLeft: 8 }}>You</span>}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && (
        <AddStaffModal onClose={() => setShowAdd(false)} onAdded={onAdded} />
      )}
      {editTarget && (
        <EditStaffModal user={editTarget} actorRole={me?.role} onClose={() => setEditTarget(null)} onSaved={onEdited} />
      )}
      {resetTarget && (
        <ResetPasswordModal user={resetTarget} onClose={() => setResetTarget(null)} onSaved={() => onResetDone(resetTarget)} />
      )}
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={doAction}
        title={
          confirm?.kind === "suspend"
            ? `Suspend ${confirm?.user?.full_name}?`
            : confirm?.kind === "reactivate"
            ? `Reactivate ${confirm?.user?.full_name}?`
            : `Remove ${confirm?.user?.full_name}?`
        }
        description={
          confirm?.kind === "suspend"
            ? "They will be immediately logged out and cannot log back in."
            : confirm?.kind === "reactivate"
            ? "They will be able to log in again."
            : "They will no longer be able to log in. All their records are preserved."
        }
        confirmLabel={
          confirm?.kind === "suspend" ? "Yes, suspend" : confirm?.kind === "reactivate" ? "Yes, reactivate" : "Yes, remove"
        }
        testid={`staff-${confirm?.kind || "action"}-confirm`}
      />
    </div>
  );
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center p-4" style={{ background: "rgba(59,47,30,0.5)" }} onClick={onClose}>
      <div className="se-card w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading" style={{ fontSize: 20 }}>{title}</h3>
          <button type="button" className="se-btn se-btn-ghost" onClick={onClose} style={{ padding: 6 }}><X size={14} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function AddStaffModal({ onClose, onAdded }) {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", role: "groom", password: "", confirm: "", force_password_change: false });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password.length < 8) return setErr("Password must be at least 8 characters.");
    if (form.password !== form.confirm) return setErr("Passwords do not match.");
    setBusy(true);
    try {
      const { data } = await api.post("/users", {
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        role: form.role,
        password: form.password,
        force_password_change: !!form.force_password_change,
      });
      onAdded(data);
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Add staff member" onClose={onClose} testid="staff-add-form">
      <form onSubmit={submit}>
        <label className="se-label block mb-1">Full name *</label>
        <input className="se-input mb-3" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="staff-add-name" />
        <label className="se-label block mb-1">Email *</label>
        <input type="email" className="se-input mb-3" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-add-email" />
        <label className="se-label block mb-1">Phone</label>
        <input className="se-input mb-3" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="staff-add-phone" />
        <label className="se-label block mb-1">Role *</label>
        <select className="se-input mb-3" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="staff-add-role">
          {MANAGEABLE_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <p className="text-xs mb-3" style={{ color: "#8C9E8A" }}>Admin role cannot be created here — only via stable onboarding.</p>
        <label className="se-label block mb-1">Password * (min 8 characters)</label>
        <input type="password" className="se-input mb-3" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-add-password" autoComplete="new-password" />
        <label className="se-label block mb-1">Confirm password *</label>
        <input type="password" className="se-input mb-3" required minLength={8} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="staff-add-password-confirm" autoComplete="new-password" />
        <label className="flex items-start gap-2 mb-3 cursor-pointer text-sm">
          <input type="checkbox" checked={form.force_password_change} onChange={(e) => setForm({ ...form, force_password_change: e.target.checked })} data-testid="staff-add-force-change" />
          Require password change at next login
        </label>
        <p className="text-xs mb-3" style={{ color: "#8C9E8A" }}>
          No email will be sent. Share the login details with the staff member directly.
        </p>
        {err && <div className="text-sm px-3 py-2 rounded-lg mb-3" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid="staff-add-error">{err}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" className="se-btn se-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="se-btn" disabled={busy} data-testid="staff-add-submit"><Save size={14} /> {busy ? "Adding…" : "Add staff"}</button>
        </div>
      </form>
    </Modal>
  );
}

function EditStaffModal({ user, actorRole, onClose, onSaved }) {
  const [form, setForm] = useState({
    full_name: user.full_name, email: user.email,
    phone: user.phone || "", role: user.role,
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  // Roles available: managers cannot edit admin accounts (already filtered); admin cannot demote sole admin (backend enforces)
  const roleOptions = actorRole === "manager"
    ? MANAGEABLE_ROLES
    : [{ value: "admin", label: "Admin" }, ...MANAGEABLE_ROLES];
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        role: form.role,
      };
      await api.patch(`/users/${user.id}`, payload);
      onSaved();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Edit ${user.full_name}`} onClose={onClose} testid="staff-edit-form">
      <form onSubmit={submit}>
        <label className="se-label block mb-1">Full name</label>
        <input className="se-input mb-3" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="staff-edit-name" />
        <label className="se-label block mb-1">Email</label>
        <input type="email" className="se-input mb-3" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-edit-email" />
        <label className="se-label block mb-1">Phone</label>
        <input className="se-input mb-3" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="staff-edit-phone" />
        <label className="se-label block mb-1">Role</label>
        <select className="se-input mb-3" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="staff-edit-role">
          {roleOptions.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        {err && <div className="text-sm px-3 py-2 rounded-lg mb-3" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid="staff-edit-error">{err}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" className="se-btn se-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="se-btn" disabled={busy} data-testid="staff-edit-submit"><Save size={14} /> {busy ? "Saving…" : "Save changes"}</button>
        </div>
      </form>
    </Modal>
  );
}

function ResetPasswordModal({ user, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [force, setForce] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) return setErr("Password must be at least 8 characters.");
    if (pw !== confirm) return setErr("Passwords do not match.");
    setBusy(true);
    try {
      await api.post(`/users/${user.id}/password-reset`, { new_password: pw, force_password_change: force });
      onSaved();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Reset password — ${user.full_name}`} onClose={onClose} testid="staff-reset-form">
      <form onSubmit={submit}>
        <label className="se-label block mb-1">New password *</label>
        <input type="password" className="se-input mb-3" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="staff-reset-password" autoComplete="new-password" />
        <label className="se-label block mb-1">Confirm new password *</label>
        <input type="password" className="se-input mb-3" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="staff-reset-confirm" autoComplete="new-password" />
        <label className="flex items-start gap-2 mb-3 cursor-pointer text-sm">
          <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="staff-reset-force-change" />
          Require password change at next login
        </label>
        <p className="text-xs mb-3" style={{ color: "#8C9E8A" }}>
          Share the new password with {user.full_name} directly. No email will be sent.
        </p>
        {err && <div className="text-sm px-3 py-2 rounded-lg mb-3" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid="staff-reset-error">{err}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" className="se-btn se-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="se-btn" disabled={busy} data-testid="staff-reset-submit"><Save size={14} /> {busy ? "Updating…" : "Update password"}</button>
        </div>
      </form>
    </Modal>
  );
}
